import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setScrollTargetJobId } from '../../../redux/JobSlice/jobSlice';
import {
    sendEmail,
    markTaskDone,
    makeDoneKey,
    executeConnectionTask
} from '../../../redux/DailyTasksSlice/dailyTaskSlice';
import {
    Linkedin,
    Link as LinkIcon,
    CheckCircle2,
    ExternalLink,
    UserPlus,
    Building2,
    FileText,
    Loader2,
    Mail,
    X,
    RefreshCw,
    Sparkles,
    Circle,
    Check
} from 'lucide-react';
import api from '../../../api/axios';
import { toast } from 'react-toastify';
import EmailGenerationModal from '../../NetworkingPage/NetContent/EmailGenerationModal';
import {
    checkInviteStatus,
    linkedInLogin,
    checkLinkedInConnectionStatus,
    isUsableProviderId
} from '../../../redux/LIAuthSlice/liAuthSlice';

const inferRoleKey = (person = {}) => {
    const title = (person?.title || '').toLowerCase();
    const predictedRole = (person?.predicted_role || '').toLowerCase();

    if (predictedRole === 'recruiter' || title.includes('recruit')) return 'recruiters';
    if (
        predictedRole === 'hiring_manager' ||
        title.includes('hiring manager') ||
        title.includes('head of') ||
        title.includes('director') ||
        title.includes('manager')
    ) return 'hiringManagers';

    return 'others';
};

const inferTaskIntent = ({ currentDay, taskText = '', isGhost = false, channel = '' }) => {
    if (isGhost) return 'milestone';

    // An explicit plan-step channel beats text/day-number heuristics — the
    // completion slot must not change because the step copy was reworded.
    const normalizedChannel = String(channel || '').toLowerCase();
    if (normalizedChannel) {
        if (/email|gmail/.test(normalizedChannel)) return 'email';
        if (/\bdm\b|message|inmail/.test(normalizedChannel)) return 'message';
        if (/connect|invite|note/.test(normalizedChannel)) return 'connect';
    }

    const normalizedTask = String(taskText || '').toLowerCase();

    if (currentDay === 4 || currentDay === 6 || normalizedTask.includes('email')) return 'email';
    if (normalizedTask.includes('note')) return 'note';
    if (
        currentDay === 5 ||
        normalizedTask.includes('dm') ||
        normalizedTask.includes('message') ||
        normalizedTask.includes('inmail')
    ) return 'message';
    if (
        normalizedTask.includes('connect') ||
        normalizedTask.includes('invite') ||
        normalizedTask.includes('connection')
    ) return 'connect';

    if (currentDay === 2) return 'review';
    if (currentDay === 3) return 'message';
    return 'connect';
};

const inferTaskReason = ({
    isGhost,
    taskIntent,
    currentDay,
    currentInviteStatus,
    activeContact,
    isHighPriority
}) => {
    if (isGhost) return 'System milestone created from your active sprint strategy for this day.';

    if (currentInviteStatus === 'pending') {
        return 'This contact already has a pending invite, so this step keeps momentum without sending duplicate requests.';
    }

    if (currentInviteStatus === 'accepted' && (taskIntent === 'connect' || taskIntent === 'note')) {
        return 'You are already connected, so the next best step is to start a short, value-first conversation.';
    }

    if (taskIntent === 'email') {
        return 'Email is prioritized on this day to create a direct follow-up path outside LinkedIn activity noise.';
    }

    if (taskIntent === 'note') {
        return 'A personalized note increases acceptance odds by giving context with your connection request.';
    }

    if (taskIntent === 'message') {
        return 'This day is scheduled for direct outreach so you can convert profile interest into an active conversation.';
    }

    if (taskIntent === 'review') {
        return 'This checkpoint prevents duplicate outreach and helps you choose the right follow-up based on response state.';
    }

    if (isHighPriority) {
        return 'This contact is marked high priority due to stronger role-fit and opportunity score for faster response impact.';
    }

    if (inferRoleKey(activeContact) === 'recruiters') {
        return 'Recruiter contacts are surfaced early because they can validate fit and route your profile quickly.';
    }

    if (currentDay <= 3) {
        return 'Early sprint days focus on opening the relationship with lightweight, high-probability outreach.';
    }

    return 'This task continues your day-by-day outreach sequence to maintain consistency and increase reply probability.';
};

const DTCard = ({ contact, currentDay, contactPools = {}, taskHistory = {}, onCompleting }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector((state) => state.auth.user);
    const authUserId = user?._id || user?.id;
    const isGoogleConnected = useSelector((state) => state.auth.isGoogleConnected);
    // Field-level selectors (not the whole slice): the card only re-renders when
    // a list it actually reads changes, so marking one task doesn't re-render
    // every card on the board for unrelated slice updates.
    const linkedinDone = useSelector((state) => state.dailyTasks.linkedinDone);
    const firstEmailDone = useSelector((state) => state.dailyTasks.firstEmailDone);
    const dmDone = useSelector((state) => state.dailyTasks.dmDone);
    const followupEmailDone = useSelector((state) => state.dailyTasks.followupEmailDone);
    const doneKeys = useSelector((state) => state.dailyTasks.doneKeys);
    const sendingEmailId = useSelector((state) => state.dailyTasks.sendingEmailId);
    const sentFirstEmails = useSelector((state) => state.dailyTasks.sentFirstEmails);
    const sentFollowupEmails = useSelector((state) => state.dailyTasks.sentFollowupEmails);

    const isLinkedInConnected = useSelector((state) => state.liauth.isLinkedInConnected);
    const inviteStatusesByProviderId = useSelector((state) => state.liauth.inviteStatusesByProviderId);
    const inviteStatusesByContactId = useSelector((state) => state.liauth.inviteStatusesByContactId);
    const inviteLoadingByContact = useSelector((state) => state.liauth.inviteLoadingByContact);
    const liAuthConnecting = useSelector((state) => state.liauth.isLoading);
    const unipileAccountId = isLinkedInConnected?.accountId;

    const [isCompleting, setIsCompleting] = useState(false);
    const [isActing, setIsActing] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [showConnectChoiceModal, setShowConnectChoiceModal] = useState(false);
    // Outreach task: per-contact email/LinkedIn popup target ({ contact, mode }).
    const [outreachPopup, setOutreachPopup] = useState(null);
    const [modalSubMode, setModalSubMode] = useState('message');
    const [selectedRoleKey, setSelectedRoleKey] = useState(contact?._suggestedRoleKey || inferRoleKey(contact));
    const [selectedPersonId, setSelectedPersonId] = useState(contact?.contactid || contact?._id || contact?._suggestedContact?.contactid || '');

    const isGhost = contact?.type === 'ghost';

    /** Valid apply URL from task payload (including stale listings—user may still open and check). */
    const ghostApplyHref = useMemo(() => {
        if (!isGhost) return null;
        const raw = String(contact?._link || '').trim();
        if (!raw || raw === '#') return null;
        return /^https?:\/\//i.test(raw) ? raw : null;
    }, [isGhost, contact?._link]);

    const stalePostingVerifyGhost = Boolean(isGhost && contact?._stalePostingVerify);

    const ghostReviewButtonClass =
        'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-colors';

    const roleBuckets = useMemo(() => {
        const recruiters = Array.isArray(contactPools?.recruiterContacts) ? contactPools.recruiterContacts : [];
        const others = Array.isArray(contactPools?.otherContacts) ? contactPools.otherContacts : [];

        const hiringManagers = [];
        const otherRoles = [];

        for (let i = 0; i < others.length; i += 1) {
            const person = others[i];
            if (inferRoleKey(person) === 'hiringManagers') {
                hiringManagers.push(person);
            } else {
                otherRoles.push(person);
            }
        }

        const withCurrentContext = (person) => ({
            ...person,
            _companyName: contact?._companyName,
            _companyLogo: contact?._companyLogo,
            _jobTitle: contact?._jobTitle,
            _jobDescription: contact?._jobDescription,
            _organizationId: contact?._organizationId,
            _strategyId: contact?._strategyId,
            _priority: contact?._priority,
            _opportunityScore: contact?._opportunityScore,
            _taskName: contact?._taskName
        });

        return {
            recruiters: recruiters.map(withCurrentContext),
            hiringManagers: hiringManagers.map(withCurrentContext),
            others: otherRoles.map(withCurrentContext)
        };
    }, [contactPools, contact]);

    const seededRoleBuckets = useMemo(() => {
        if (!isGhost || !contact?._suggestedContact) return roleBuckets;

        const suggestedContact = {
            ...contact._suggestedContact,
            _companyName: contact?._companyName,
            _companyLogo: contact?._companyLogo,
            _jobTitle: contact?._jobTitle,
            _jobDescription: contact?._jobDescription,
            _organizationId: contact?._organizationId,
            _strategyId: contact?._strategyId,
            _priority: contact?._priority,
            _opportunityScore: contact?._opportunityScore,
            _taskName: contact?._taskName
        };

        const roleKey = contact?._suggestedRoleKey || inferRoleKey(suggestedContact);
        const hasAnyContacts = roleBuckets.recruiters.length || roleBuckets.hiringManagers.length || roleBuckets.others.length;

        if (hasAnyContacts) return roleBuckets;

        return {
            recruiters: roleKey === 'recruiters' ? [suggestedContact] : [],
            hiringManagers: roleKey === 'hiringManagers' ? [suggestedContact] : [],
            others: roleKey === 'others' ? [suggestedContact] : []
        };
    }, [isGhost, contact, roleBuckets]);

    const availableRoleOptions = useMemo(() => {
        const options = [];
        if (seededRoleBuckets.recruiters.length) options.push({ key: 'recruiters', label: 'Recruiters', count: seededRoleBuckets.recruiters.length });
        if (seededRoleBuckets.hiringManagers.length) options.push({ key: 'hiringManagers', label: 'Hiring Managers', count: seededRoleBuckets.hiringManagers.length });
        if (seededRoleBuckets.others.length) options.push({ key: 'others', label: 'Other Roles', count: seededRoleBuckets.others.length });
        return options;
    }, [seededRoleBuckets]);

    useEffect(() => {
        const nextRole = inferRoleKey(contact);
        setSelectedRoleKey(nextRole);
        setSelectedPersonId(contact?.contactid || contact?._id || '');
    }, [contact]);

    useEffect(() => {
        if (!availableRoleOptions.length) return;
        const roleExists = availableRoleOptions.some((item) => item.key === selectedRoleKey);
        if (!roleExists) {
            setSelectedRoleKey(availableRoleOptions[0].key);
        }
    }, [availableRoleOptions, selectedRoleKey]);

    const selectedRoleList = seededRoleBuckets[selectedRoleKey] || [];

    useEffect(() => {
        if (!selectedRoleList.length) return;
        const exists = selectedRoleList.some((item) => (item?.contactid || item?._id) === selectedPersonId);
        if (!exists) {
            const firstId = selectedRoleList[0]?.contactid || selectedRoleList[0]?._id || '';
            setSelectedPersonId(firstId);
        }
    }, [selectedRoleList, selectedPersonId]);

    const selectedFromList = useMemo(() => {
        for (let i = 0; i < selectedRoleList.length; i += 1) {
            const person = selectedRoleList[i];
            const id = person?.contactid || person?._id;
            if (id === selectedPersonId) return person;
        }
        return null;
    }, [selectedRoleList, selectedPersonId]);

    const activeContact = selectedFromList || contact;
    const contactId = activeContact?.contactid || activeContact?._id;
    // Outreach-sprint cards carry their own chosen people (_outreachContacts) and
    // have no role-picker; the seeded role-list selection (selectedFromList) does
    // NOT belong to them. Complete against the mission's own id — same as ghosts —
    // otherwise "Done" gets recorded under a seeded contact while the day
    // aggregates read the mission id, so the task shows Done but the counters stay
    // 0/1 and the sprint never advances.
    const isOutreachCard = Array.isArray(contact?._outreachContacts) && contact._outreachContacts.length > 0;
    /** Milestones/outreach cards complete against the row's own id, not a seeded suggested contact. */
    const completionContactId = (isGhost || isOutreachCard) ? (contact?.contactid || contact?._id || null) : contactId;
    const canVerifyInviteStatus = Boolean(isLinkedInConnected?.accountId);

    const companyName = activeContact._companyName || activeContact.organization_name || '';
    const companyLogo = activeContact._companyLogo || '';
    const jobTitle = activeContact._jobTitle || '';
    const isHighPriority = (activeContact._opportunityScore || 0) > 20;
    const currentInviteStatus = canVerifyInviteStatus
        ? (inviteStatusesByProviderId?.[activeContact?.providerId] || inviteStatusesByContactId?.[contactId])
        : undefined;

    const sprintId = contact?._sprintId || null;

    const done = useMemo(() => {
        if (!completionContactId) return false;
        const id = String(completionContactId);
        // Sprint-scoped key is authoritative; the legacy day-slot lists only
        // cover records written before sprint scoping (or Networking-page marks).
        if (sprintId && doneKeys.includes(makeDoneKey(sprintId, currentDay, id))) return true;
        if (currentDay === 4) return firstEmailDone.includes(id);
        if (currentDay === 5) return dmDone.includes(id);
        if (currentDay === 6) return followupEmailDone.includes(id);
        return linkedinDone.includes(id);
    }, [completionContactId, sprintId, doneKeys, currentDay, linkedinDone, firstEmailDone, dmDone, followupEmailDone]);

    useEffect(() => {
        if (done) setIsCompleting(false);
    }, [done]);

    const handleConnectGmail = () => {
        const baseURL = api.defaults.baseURL || process.env.REACT_APP_API_BASE_URL || window.location.origin;
        window.open(`${baseURL}/api/auth/google`, '_self');
    };

    const handleDispatchSendEmail = () => {
        const isFirstEmail = currentDay === 4;
        dispatch(sendEmail({
            to: activeContact.email,
            subject: isFirstEmail
                ? `Regarding opportunities at ${activeContact._companyName || activeContact.organization_name}`
                : `Following up about ${activeContact._companyName || activeContact.organization_name}`,
            contactName: activeContact.name,
            organizationName: activeContact._companyName || activeContact.organization_name,
            industry: activeContact.industry,
            contactId,
            step: isFirstEmail ? 2 : 4
        }));
    };

    const handleDoneClick = () => {
        if (done || isCompleting || !authUserId || !completionContactId) return;
        // Pin the board to this day BEFORE the completion lands in the store —
        // otherwise the auto-follow day derivation jumps to the next day the
        // instant the last task flips done.
        onCompleting?.();
        setIsCompleting(true);
        // taskKey comes from the step's channel/intent (historyTaskKey), not the
        // day number, and the record is scoped to this sprint step — so the same
        // contact in another sprint (or another step) completes independently.
        dispatch(markTaskDone({
            contactId: completionContactId,
            day: currentDay,
            actionTaken: taskLabel,
            taskKey: historyTaskKey,
            sprintId,
            stepIndex: sprintId ? currentDay : null
        }))
            .unwrap()
            .catch(() => {
                setIsCompleting(false);
                toast.error('Could not save this task — try again.');
            });
    };

    const taskLabel = useMemo(() => {
        if (isGhost) return contact.name || 'Submit Application';
        const explicitTaskName = typeof activeContact?._taskName === 'string' ? activeContact._taskName.trim() : '';
        if (explicitTaskName) return explicitTaskName;
        if (currentDay === 1) return 'Send connection request with a short personalized note';
        if (currentDay === 2) return 'Review connection request status and prepare follow-up';
        if (currentDay === 3) return 'Send LinkedIn message if connected, or use InMail fallback';
        if (currentDay === 4) return 'Send introduction email';
        if (currentDay === 5) return 'Follow up via LinkedIn DM';
        if (currentDay === 6) return 'Send follow-up email';
        return activeContact.title?.toLowerCase().includes('recruiter')
            ? 'Engage with recruiter content'
            : 'Send connection request';
    }, [isGhost, contact, activeContact, currentDay]);

    const taskIntent = useMemo(
        () => inferTaskIntent({ currentDay, taskText: taskLabel, isGhost, channel: contact?._dayChannel || '' }),
        [currentDay, taskLabel, isGhost, contact?._dayChannel]
    );

    const historyTaskKey = useMemo(() => {
        if (taskIntent === 'milestone') {
            if (currentDay === 4) return 'firstEmailDone';
            if (currentDay === 5) return 'dmDone';
            if (currentDay === 6) return 'followupEmailDone';
            return 'linkedinDone';
        }
        if (taskIntent === 'email') return currentDay === 6 ? 'followupEmailDone' : 'firstEmailDone';
        if (taskIntent === 'message') return 'dmDone';
        return 'linkedinDone';
    }, [taskIntent, currentDay]);

    const completedMeta = taskHistory?.[historyTaskKey] || null;

    const completedAtLabel = useMemo(() => {
        if (!completedMeta?.doneAt) return null;

        const doneDate = new Date(completedMeta.doneAt);
        if (Number.isNaN(doneDate.getTime())) return null;

        return doneDate.toLocaleString();
    }, [completedMeta]);

    const taskType = useMemo(() => {
        if (taskIntent === 'milestone') return 'Milestone';
        if (taskIntent === 'email') return 'Email';
        if (taskIntent === 'note') return 'LinkedIn Note';
        if (taskIntent === 'message') return 'LinkedIn Message';
        if (taskIntent === 'review') return 'Review';
        return 'Connect';
    }, [taskIntent]);

    // v2 tag tones: Apply (indigo) · Outreach (purple) · Network (green)
    const tagTone = useMemo(() => {
        if (taskIntent === 'milestone') return { bg: '#EEF1FE', fg: '#3F57E0' };       // Apply
        if (taskIntent === 'email' || taskIntent === 'note' || taskIntent === 'message') return { bg: '#F5F3FF', fg: '#7C3AED' }; // Outreach
        return { bg: '#ECFDF5', fg: '#047857' };                                       // Network (connect / review)
    }, [taskIntent]);

    const taskReason = useMemo(() => {
        const explicitReason = typeof activeContact?._taskReason === 'string' ? activeContact._taskReason.trim() : '';
        if (explicitReason) return explicitReason;

        return inferTaskReason({
            isGhost,
            taskIntent,
            currentDay,
            currentInviteStatus,
            activeContact,
            isHighPriority
        });
    }, [isGhost, taskIntent, currentDay, currentInviteStatus, activeContact, isHighPriority]);

    const roleLabel = useMemo(() => {
        if (isGhost) return null;
        if (activeContact.predicted_role === 'hiring_manager') return { label: 'Decision maker', color: 'text-amber-700 bg-amber-50 border-amber-100' };
        if (inferRoleKey(activeContact) === 'others') return { label: 'Team contact', color: 'text-slate-700 bg-slate-50 border-slate-200' };
        return { label: 'Recruiter', color: 'text-[#3F57E0] bg-indigo-50 border-indigo-100' };
    }, [isGhost, activeContact]);

    // Fetch invite status once (when unknown). If it's pending, we show it as a stable state
    // and avoid repeated polling/spinners.
    const requestedStatusRef = useRef(new Set());
    useEffect(() => {
        const pid = activeContact?.providerId;
        const aid = isLinkedInConnected?.accountId;

        if (!aid || !isUsableProviderId(pid)) return;
        if (inviteStatusesByProviderId?.[pid] !== undefined) return;
        if (requestedStatusRef.current.has(pid)) return;

        requestedStatusRef.current.add(pid);
        dispatch(checkInviteStatus({ accountId: aid, providerId: pid }));
    }, [activeContact?.providerId, isLinkedInConnected?.accountId, inviteStatusesByProviderId, dispatch]);

    const handleUnipileConnect = async () => {
        if (!isLinkedInConnected?.accountId || !contactId || isActing) return;
        
        // Try to find a real database taskId if possible
        const taskId = activeContact._id || contact?._id || contactId;
        
        setIsActing(true);
        try {
            await dispatch(executeConnectionTask({
                taskId,
                text: "", // Standard invite
                providerId: activeContact?.providerId,
                contactId: contactId,
                profileUrl: activeContact.linkedin_url,
                accountId: isLinkedInConnected.accountId
            })).unwrap();
            // Persist the sprint-scoped completion — the invite fallback path
            // doesn't write DailyTaskProgress on the server, so without this the
            // "done" state evaporated on reload.
            handleDoneClick();
        } catch (err) {
            console.error("Failed to send automated connection request:", err);
        } finally {
            setIsActing(false);
        }
    };

    /** Same hosted-auth popup flow as Networking — connect LinkedIn without leaving Daily Tasks. */
    const handleStartLinkedInConnection = async () => {
        if (!authUserId) {
            toast.error('Please sign in to connect LinkedIn.');
            return;
        }

        const authWindow = window.open(
            'about:blank',
            'LinkedIn Authentication',
            'width=600,height=700,left=200,top=100'
        );

        try {
            const response = await dispatch(linkedInLogin()).unwrap();
            const authUrl = response?.authUrl || response?.url || response?.data?.authUrl || response?.data?.url;

            if (!authUrl) {
                if (authWindow && !authWindow.closed) authWindow.close();
                toast.error('Could not start LinkedIn connection. Try again.');
                return;
            }

            if (authWindow) {
                authWindow.location.href = authUrl;
                const pollTimer = setInterval(() => {
                    if (authWindow.closed) {
                        clearInterval(pollTimer);
                        dispatch(checkLinkedInConnectionStatus());
                    }
                }, 1000);
            } else {
                window.open(authUrl, '_blank', 'noopener,noreferrer');
            }
        } catch (err) {
            if (authWindow && !authWindow.closed) authWindow.close();
            const msg = typeof err === 'string' ? err : err?.message || 'LinkedIn connection failed.';
            toast.error(msg);
        }
    };

    const handleSendNoteClick = () => {
        setModalSubMode('note');
        setShowEmailModal(true);
    };

    const handleMessageClick = () => {
        setModalSubMode('message');
        setShowEmailModal(true);
    };

    const handleNoteTaskClick = () => {
        if (!unipileAccountId) {
            handleStartLinkedInConnection();
            return;
        }
        handleSendNoteClick();
    };

    const handleConnectClick = () => {
        if (!unipileAccountId) {
            handleStartLinkedInConnection();
            return;
        }

        setShowConnectChoiceModal(true);
    };

    const handleSendWithoutNote = async () => {
        setShowConnectChoiceModal(false);
        await handleUnipileConnect();
    };

    const handleSendWithNote = () => {
        setShowConnectChoiceModal(false);
        handleSendNoteClick();
    };

    const actionBarClass = 'flex flex-nowrap items-center gap-3 mt-4 w-full min-w-0';
    const actionBarPrimaryClass = 'flex flex-wrap items-center gap-3 flex-1 min-w-0';

    const renderDoneIconButton = ({ enforceCompletionId = false } = {}) => {
        const blockedById = enforceCompletionId && !completionContactId;

        // Completed → unmistakable green "Done" badge (not actionable).
        // Height/padding match the sibling Message/Profile buttons (px-4 py-2.5 text-sm).
        if (done) {
            return (
                <span
                    className="inline-flex items-center gap-1.5 shrink-0 px-4 py-2.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-bold"
                    title="Completed"
                >
                    <span className="w-4 h-4 rounded-full bg-emerald-600 flex items-center justify-center">
                        <Check size={11} className="text-white" strokeWidth={3.5} />
                    </span>
                    Done
                </span>
            );
        }

        // Saving in progress.
        if (isCompleting) {
            return (
                <span className="inline-flex items-center gap-1.5 shrink-0 px-4 py-2.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-600 text-sm font-bold cursor-wait">
                    <Loader2 size={16} className="animate-spin" /> Saving…
                </span>
            );
        }

        // Not done → clearly an action, with an empty circle so it doesn't read as "done".
        return (
            <button
                type="button"
                onClick={handleDoneClick}
                disabled={blockedById}
                aria-label="Mark done"
                title={blockedById ? 'Not available yet' : 'Mark this task done'}
                className={`inline-flex items-center gap-1.5 shrink-0 px-4 py-2.5 rounded-lg border text-sm font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50 ${
                    blockedById
                        ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 active:scale-95'
                }`}
            >
                <Circle size={16} /> Mark done
            </button>
        );
    };

    const renderPrimaryActions = () => {
        if (isGhost) {
            const baseJobId = activeContact?._baseJobId || contact?._baseJobId || null;
            return (
                <div className={actionBarClass}>
                    <div className={actionBarPrimaryClass}>
                        {ghostApplyHref ? (
                            <a
                                href={ghostApplyHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-200 transition-all border border-slate-200"
                            >
                                <ExternalLink size={14} /> Open Application
                            </a>
                        ) : null}
                        {stalePostingVerifyGhost ? (
                            <button
                                type="button"
                                onClick={() => navigate('/savedjobs')}
                                className={ghostReviewButtonClass}
                                title="Open saved jobs"
                            >
                                Review
                            </button>
                        ) : !ghostApplyHref ? (
                            <button
                                type="button"
                                onClick={() => {
                                    navigate('/savedjobs', {
                                        state: baseJobId ? { scrollSavedJobId: baseJobId } : undefined
                                    });
                                }}
                                className={ghostReviewButtonClass}
                                title="No application URL on file for this job"
                            >
                                <ExternalLink size={14} /> Open in My Jobs
                            </button>
                        ) : null}
                    </div>
                    <div className="shrink-0 flex items-center">
                        {renderDoneIconButton({ enforceCompletionId: true })}
                    </div>
                </div>
            );
        }

        // Email tasks
        if (taskIntent === 'email') {
            if (activeContact?.email) {
                const sentList = currentDay === 4 ? sentFirstEmails : sentFollowupEmails;
                const hasBeenSent = sentList.includes(contactId) || done;

                if (hasBeenSent) {
                    return (
                        <div className="mt-4">
                            <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-emerald-50 text-emerald-600 border border-emerald-200 cursor-default">
                                <CheckCircle2 size={16} /> Email Sent
                            </span>
                        </div>
                    );
                }

                return (
                    <div className="mt-4">
                        <button
                            onClick={() => setShowEmailModal(true)}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 hover:shadow-md transition-all active:scale-95 shadow-sm"
                        >
                            <Mail size={16} /> Generate Email
                        </button>
                    </div>
                );
            }

            if (isGoogleConnected) {
                return (
                    <div className="mt-4">
                        <button
                            onClick={handleDispatchSendEmail}
                            disabled={sendingEmailId === contactId}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 hover:shadow-md transition-all active:scale-95 shadow-sm disabled:opacity-50"
                        >
                            {sendingEmailId === contactId ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <Mail size={16} />
                            )}
                            {sendingEmailId === contactId ? 'Sending...' : 'Send Email'}
                        </button>
                    </div>
                );
            }

            return (
                <div className="mt-4">
                    <button
                        onClick={handleConnectGmail}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-bold hover:bg-purple-700 hover:shadow-md transition-all active:scale-95 shadow-sm"
                    >
                        <LinkIcon size={16} /> Connect Gmail
                    </button>
                </div>
            );
        }

        if (taskIntent === 'message') {
            return (
                <div className={actionBarClass}>
                    <div className={actionBarPrimaryClass}>
                        <button
                            onClick={handleMessageClick}
                            disabled={isCompleting || isActing}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md transition-all active:scale-95 shadow-sm disabled:opacity-70"
                        >
                            {isActing ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Message
                        </button>
                        <a
                            href={activeContact.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-600 rounded-lg text-sm font-bold border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all active:scale-95 shadow-sm"
                        >
                            <ExternalLink size={16} /> Profile
                        </a>
                    </div>
                    <div className="shrink-0 flex items-center">
                        {renderDoneIconButton()}
                    </div>
                </div>
            );
        }

        if (taskIntent === 'note') {
            return (
                <div className={actionBarClass}>
                    <div className={actionBarPrimaryClass}>
                        <button
                            onClick={handleNoteTaskClick}
                            disabled={isCompleting || isActing || liAuthConnecting}
                            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all shadow-sm ${
                                !unipileAccountId
                                    ? 'bg-amber-600 text-white hover:bg-amber-700 active:scale-95 disabled:opacity-70'
                                    : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md active:scale-95 disabled:opacity-70'
                            }`}
                            title={
                                !unipileAccountId
                                    ? 'Connect LinkedIn in a popup, then return here to send your note'
                                    : 'Generate and send connection note'
                            }
                        >
                            {isActing || (!unipileAccountId && liAuthConnecting) ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <Mail size={16} />
                            )}
                            {unipileAccountId
                                ? 'Send Note + Connect'
                                : liAuthConnecting
                                    ? 'Connecting…'
                                    : 'Connect LinkedIn'}
                        </button>
                        <a
                            href={activeContact.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-600 rounded-lg text-sm font-bold border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all active:scale-95 shadow-sm"
                        >
                            <ExternalLink size={16} /> Profile
                        </a>
                    </div>
                    <div className="shrink-0 flex items-center">
                        {renderDoneIconButton()}
                    </div>
                </div>
            );
        }

        const isActionLoading = inviteLoadingByContact[contactId] || isCompleting || isActing;

        const profileUrl = activeContact.linkedin_url;

        return (
            <div className={actionBarClass}>
                <div className={`${actionBarPrimaryClass} items-center`}>
                    {/* Connect Button */}
                    {currentInviteStatus === 'accepted' ? (
                        <button
                            disabled
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-emerald-50 text-emerald-600 border border-emerald-200 cursor-default"
                        >
                            <CheckCircle2 size={16} /> Connected
                        </button>
                    ) : currentInviteStatus === 'pending' ? (
                        <button
                            disabled
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-amber-50 text-amber-600 border border-amber-200 cursor-default"
                        >
                            Pending
                        </button>
                    ) : currentInviteStatus === 'failed' ? (
                        <div className="flex items-center gap-1.5">
                            <button
                                onClick={handleUnipileConnect}
                                disabled={isActing}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 transition-all"
                                title="Automation failed. Click to retry."
                            >
                                <RefreshCw size={14} /> Retry
                            </button>
                            <a
                                href={profileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 transition-all"
                                title="Open profile to connect manually"
                            >
                                <ExternalLink size={14} /> Manual
                            </a>
                        </div>
                    ) : (
                        <button
                            onClick={handleConnectClick}
                            disabled={(isActionLoading && !!unipileAccountId) || (!unipileAccountId && liAuthConnecting)}
                            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all shadow-sm ${
                                !unipileAccountId
                                    ? 'bg-amber-600 text-white hover:bg-amber-700 active:scale-95 disabled:opacity-70'
                                    : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md active:scale-95 disabled:opacity-70'
                            }`}
                            title={
                                !unipileAccountId
                                    ? 'Connect LinkedIn in a popup, then send the invite from this card'
                                    : 'Send connection request'
                            }
                        >
                            {(isActionLoading && !!unipileAccountId) || (!unipileAccountId && liAuthConnecting) ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <UserPlus size={16} />
                            )}
                            {!unipileAccountId ? (liAuthConnecting ? 'Connecting…' : 'Connect LinkedIn') : 'Connect'}
                        </button>
                    )}

                    {/* Profile Button */}
                    <a
                        href={profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-600 rounded-lg text-sm font-bold border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all active:scale-95 shadow-sm"
                    >
                        <ExternalLink size={16} /> Profile
                    </a>
                </div>

                <div className="shrink-0 flex items-center">
                    {renderDoneIconButton()}
                </div>
            </div>
        );
    };



    // ── Outreach task card ──────────────────────────────────────────────────
    // For strategies activated from the outreach modal: show the day's action +
    // the chosen "who to contact" people, each with a channel-aware action
    // (Gmail/email on Email days, LinkedIn on LinkedIn/DM days). Additive — the
    // legacy card logic below is untouched.
    const outreachPeople = Array.isArray(contact?._outreachContacts) ? contact._outreachContacts : [];
    if (outreachPeople.length) {
        const channel = String(contact?._dayChannel || '');
        const isEmailChannel = /email|gmail/i.test(channel);
        // LinkedIn popup sub-mode: a "DM"/message vs a connection-request note.
        const linkedinMode = /\bdm\b|message/i.test(channel) ? 'message' : 'note';
        const accent = '#3F57E0';

        return (
            <div
                className={`relative px-5 py-4 rounded-xl border shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-300 text-left ${
                    done ? 'bg-slate-50/70 border-slate-200' : 'bg-white border-[#ECECEC]'
                }`}
                style={{ borderLeftWidth: 3, borderLeftColor: done ? '#10B981' : accent, opacity: done ? 0.92 : 1 }}
            >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            {contact?._pathTitle && (
                                <span className="text-[10px] font-bold uppercase tracking-wide text-[#3F57E0] bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full">
                                    {contact._pathTitle}
                                </span>
                            )}
                            {channel && (
                                <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{channel}</span>
                            )}
                        </div>
                        <h3 className="text-[14px] font-bold text-slate-900 mt-2 leading-snug">{taskLabel}</h3>
                        {contact?._dayGoal && (
                            <p className="text-[11.5px] text-slate-400 mt-1 flex items-center gap-1">
                                <Sparkles size={12} className="text-[#3F57E0]" /> {contact._dayGoal}
                            </p>
                        )}
                    </div>
                    <div className="shrink-0">{renderDoneIconButton()}</div>
                </div>

                {/* Who to contact */}
                <div className="mt-3.5 pt-3.5 border-t border-[#F1F3F7]">
                    <p className="text-[10px] font-bold uppercase tracking-[0.06em] text-slate-400 mb-2">Who to contact</p>
                    <div className="flex flex-col gap-1.5">
                        {outreachPeople.map((person, i) => {
                            const canEmail = isEmailChannel && Boolean(person?.email);
                            return (
                                <div key={person?.contactid || person?._id || i} className="flex items-center justify-between gap-3 rounded-lg border border-[#EEF0F4] px-3 py-2">
                                    <div className="min-w-0">
                                        <p className="text-[12.5px] font-semibold text-slate-800 truncate">{person?.name || 'Unknown'}</p>
                                        {person?.title && <p className="text-[11px] text-slate-400 truncate">{person.title}</p>}
                                    </div>
                                    {isEmailChannel ? (
                                        <button
                                            type="button"
                                            onClick={() => setOutreachPopup({ contact: person, mode: 'email' })}
                                            disabled={!person?.email}
                                            title={person?.email ? 'Generate email' : 'No email available'}
                                            className="inline-flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-lg text-[12px] font-bold transition-all active:scale-95 disabled:opacity-40 bg-[#3F57E0] text-white hover:bg-[#3349c9]"
                                        >
                                            <Mail size={13} /> Generate email
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => setOutreachPopup({ contact: person, mode: linkedinMode })}
                                            title="Generate LinkedIn message"
                                            className="inline-flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-lg text-[12px] font-bold transition-all active:scale-95 bg-[#0A66C2] text-white hover:brightness-110"
                                        >
                                            <Linkedin size={13} /> Generate message
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <EmailGenerationModal
                    isOpen={!!outreachPopup}
                    onClose={() => setOutreachPopup(null)}
                    contact={outreachPopup?.contact || {}}
                    outreachMode={outreachPopup?.mode || 'email'}
                    jobData={{
                        jobPosition: contact?._jobTitle || '',
                        jobDescription: contact?._jobDescription || '',
                        description: contact?._jobDescription || ''
                    }}
                    companyData={{
                        name: contact?._companyName || '',
                        industry: contact?.industry || ''
                    }}
                />
            </div>
        );
    }

    return (
        <div
            className={`relative flex items-start gap-4 px-5 py-4 rounded-xl border shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-300 text-left ${
                done
                    ? 'bg-slate-50/70 border-slate-200'
                    : 'bg-white border-[#ECECEC] hover:border-slate-300'
            }`}
            style={{ borderLeftWidth: 3, borderLeftColor: done ? '#10B981' : '#3F57E0', opacity: done ? 0.9 : 1 }}
        >
            <div className="relative shrink-0 mt-0.5">
                {isGhost ? (
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-[#3F57E0]">
                        <FileText size={18} />
                    </div>
                ) : activeContact.profile_picture ? (
                    <>
                        <img
                            src={activeContact.profile_picture}
                            alt={activeContact.name}
                            className={`w-10 h-10 rounded-lg object-cover border border-slate-200 transition-all duration-300 ${done ? 'opacity-50 grayscale' : ''}`}
                        />
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[#0077B5] rounded-full border-2 border-white flex items-center justify-center">
                            <Linkedin size={8} className="text-white fill-white" />
                        </div>
                    </>
                ) : (
                    <>
                        <div className={`w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center transition-all duration-300 ${done ? 'opacity-50' : ''}`}>
                            <Building2 size={18} className="text-slate-400" />
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[#0077B5] rounded-full border-2 border-white flex items-center justify-center">
                            <Linkedin size={8} className="text-white fill-white" />
                        </div>
                    </>
                )}
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <span className={`text-sm font-semibold truncate transition-all duration-300 ${done ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                                {activeContact.name || 'Unknown'}
                            </span>
                            {roleLabel && (
                                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${roleLabel.color}`}>
                                    {roleLabel.label}
                                </span>
                            )}
                            {isHighPriority && !done && (
                                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 border border-amber-100 text-amber-700">
                                    High priority
                                </span>
                            )}
                        </div>
                        {!isGhost && activeContact.title && (
                            <p className={`text-xs truncate transition-all duration-300 ${done ? 'text-slate-400' : 'text-slate-500'}`}>
                                {activeContact.title}
                            </p>
                        )}
                        {(companyName || jobTitle) && (
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                {companyLogo ? (
                                    <img
                                        src={companyLogo}
                                        alt={companyName}
                                        className="w-4 h-4 rounded object-cover border border-slate-200"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                        }}
                                    />
                                ) : companyName ? (
                                    <Building2 size={12} className="text-slate-400 shrink-0" />
                                ) : null}
                                {companyName && (
                                    <span className={`text-xs font-medium ${done ? 'text-slate-400' : 'text-slate-600'}`}>
                                        {companyName}
                                    </span>
                                )}
                                {jobTitle && (
                                    <>
                                        <span className="text-slate-300 text-xs">•</span>
                                        <span className={`text-xs truncate max-w-[180px] ${done ? 'text-slate-400' : 'text-slate-500'}`}>
                                            {jobTitle}
                                        </span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <span
                        className="shrink-0 text-[10px] font-bold uppercase tracking-[0.04em] px-2 py-0.5 rounded-full"
                        style={{ background: done ? '#F1F5F9' : tagTone.bg, color: done ? '#94A3B8' : tagTone.fg }}
                    >
                        {taskType}
                    </span>
                </div>
                <p className={`mt-2 text-xs leading-5 font-medium ${done ? 'text-slate-400' : 'text-slate-700'}`}>
                    {taskLabel}
                </p>
                <div className="mt-2.5 flex items-start gap-2 px-3 py-2 rounded-lg bg-[#FAFBFD] border border-[#EFF1F5]">
                    <Sparkles size={12} className="text-[#3F57E0] shrink-0 mt-0.5" />
                    <span className="text-[11px] leading-relaxed text-slate-500">
                        <b className="text-slate-700">Why this task:</b> {taskReason}
                    </span>
                </div>
                {done && (completedMeta?.completedDay || completedMeta?.actionTaken || completedAtLabel) && (
                    <p className="mt-1 text-[11px] text-emerald-700 font-medium">
                        {completedMeta?.completedDay ? `Done on day ${completedMeta.completedDay}` : 'Done'}
                        {completedMeta?.actionTaken ? ` • ${completedMeta.actionTaken}` : ''}
                        {completedAtLabel ? ` • ${completedAtLabel}` : ''}
                    </p>
                )}
                <div className="mt-1">
                    {renderPrimaryActions()}
                </div>

                {availableRoleOptions.length > 0 && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2">
                            <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                                Role
                            </label>
                            <select
                                value={selectedRoleKey}
                                onChange={(e) => setSelectedRoleKey(e.target.value)}
                                className="w-full text-xs bg-white border border-slate-200 rounded-md px-2 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                            >
                                {availableRoleOptions.map((role) => (
                                    <option key={role.key} value={role.key}>
                                        {role.label} ({role.count})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2">
                            <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                                Person
                            </label>
                            <select
                                value={selectedPersonId}
                                onChange={(e) => setSelectedPersonId(e.target.value)}
                                className="w-full text-xs bg-white border border-slate-200 rounded-md px-2 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                            >
                                {selectedRoleList.map((person, idx) => {
                                    const personId = person?.contactid || person?._id || `${selectedRoleKey}-${idx}`;
                                    const personTitle = person?.title ? ` - ${person.title}` : '';
                                    return (
                                        <option key={personId} value={personId}>
                                            {`${person?.name || 'Unknown'}${personTitle}`}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    </div>
                )}
            </div>

            <EmailGenerationModal
                isOpen={showEmailModal}
                onClose={() => setShowEmailModal(false)}
                onSuccess={(meta) => {
                    if (meta?.automated) handleDoneClick();
                }}
                taskId={activeContact._id || contact?._id || contactId}
                contact={activeContact}
                inviteStatus={currentInviteStatus}
                outreachMode={currentDay === 4 || currentDay === 6 ? 'email' : modalSubMode}
                jobData={{
                    jobPosition: activeContact?._jobTitle || '',
                    jobDescription: activeContact?._jobDescription || '',
                    description: activeContact?._jobDescription || ''
                }}
                companyData={{
                    name: activeContact?._companyName || activeContact?.organization_name || '',
                    industry: activeContact?.industry || ''
                }}
            />

            {showConnectChoiceModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                    onClick={() => setShowConnectChoiceModal(false)}
                >
                    <div
                        className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-sm font-semibold text-slate-900">Connect options</p>
                                <p className="mt-1 text-xs leading-5 text-slate-500">
                                    Send a clean invite or open the AI note draft first.
                                </p>
                            </div>
                            <button
                                onClick={() => setShowConnectChoiceModal(false)}
                                className="text-slate-400 hover:text-slate-600"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="mt-4 space-y-2">
                            <button
                                onClick={handleSendWithoutNote}
                                className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition-all hover:bg-blue-700"
                            >
                                <UserPlus size={16} />
                                Send without note
                            </button>
                            <button
                                onClick={handleSendWithNote}
                                className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition-all hover:bg-slate-50"
                            >
                                <Mail size={16} />
                                Send with note
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Memoized: the board renders many cards and parent state changes often; with
// stable props (memoized missions + sentinel objects) unrelated cards skip
// re-rendering entirely.
export default React.memo(DTCard);
